/**
 * Law tab: charge-filtered browser over the CASELAW authorities database
 * (statutes, case law, court rules) with a pill selector, a highlighted
 * full-text detail panel, and print/save actions.
 *
 * Faithful port of the monolith's renderLawTab / showLawSubTab / renderLawContent
 * / highlightLawText / selectLawEntry / goToLaw and the print/save builders.
 *
 * Note: the in-scope Law functions read authorities from the static CASELAW
 * database only; they do NOT consult the MGL search singleton (getMglSearch()).
 * MGL statute lookup lives in separate (out-of-scope) statute-modal functions.
 */

import type { ChargeSlug } from '@/types';
import { CASELAW } from '@/data/caselaw';
import { CHARGES } from '@/data/charges';
import { getDetectedCharges, allChargeText } from '@/core/citations';
import { store } from '@/app/store';
import { byId } from '@/ui/dom';
import { showToast, showModal } from '@/ui/overlays';
import { printPlainText } from '@/ui/print';
import { showPage } from '@/ui/router';
import { linkifyElement } from '@/features/law-cite';

// ── Module state ──────────────────────────────────────────────────────────
let lawActiveSubTab = 'all';
let lawActiveEntry: string | null = null;
/**
 * Set only by the two paths where the user asked to READ an authority —
 * selectLawEntry (tapping a pill) and goToLaw (following a citation chip).
 * renderLawContent() also runs on tab and sub-tab switches, and popping the
 * modal open on those would make the Law tab impossible to browse.
 */
let pendingModalOpen = false;

/** Escape &, <, > for safe HTML interpolation (matches the monolith's escH). */
function escH(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Charges detected from the current case's charge description. */
function detectedCharges(): ChargeSlug[] {
  return getDetectedCharges(allChargeText(store.getState().chargeList, store.getState().chargeDescription));
}

// ── Sub-tab switching ─────────────────────────────────────────────────────
export function showLawSubTab(type: string): void {
  lawActiveSubTab = type;
  // Update active ctab
  (['all', 'caselaw', 'statute', 'rule'] as const).forEach(function (t) {
    const k = t === 'caselaw' ? 'case' : t === 'statute' ? 'stat' : t;
    const btn = byId('ltab-law-' + k);
    if (btn) btn.classList.toggle('active', t === type);
  });
  renderLawContent();
}

export function renderLawTab(): void {
  const charges = detectedCharges();
  const subtitle = byId('law-subtitle');
  if (subtitle) {
    const chargeLabels = charges.map(function (k) {
      return CHARGES[k] ? CHARGES[k].label : k;
    });
    subtitle.textContent = chargeLabels.length
      ? 'Authorities for: ' + chargeLabels.join(', ')
      : 'All applicable authorities — enter charges on the Case tab to filter';
  }
  renderLawContent();
}

// Highlight applicableSegments within fullText
function highlightLawText(fullText: string, segments: string[]): string {
  if (!fullText) return '';
  if (!segments || !segments.length)
    return fullText
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
  // Build a combined regex that matches any segment (longest first to avoid partial overlaps)
  const sorted = segments.slice().sort(function (a, b) {
    return b.length - a.length;
  });
  const escaped = sorted.map(function (s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  });
  const re = new RegExp('(' + escaped.join('|') + ')', 'gi');
  // Split fullText on newlines, process each line
  const lines = fullText.split('\n');
  return lines
    .map(function (line) {
      let safeIdx = 0;
      let out = '';
      let match: RegExpExecArray | null;
      re.lastIndex = 0;
      while ((match = re.exec(line)) !== null) {
        // text before match
        out += escH(line.slice(safeIdx, match.index));
        // highlighted match
        out +=
          '<mark style="background:#fef08a;padding:1px 3px;border-radius:3px;font-weight:600;">' +
          escH(match[0]) +
          '</mark>';
        safeIdx = match.index + match[0].length;
      }
      out += escH(line.slice(safeIdx));
      return out;
    })
    .join('<br>');
}

export function selectLawEntry(id: string): void {
  // Tapping the active pill used to collapse the inline panel. The modal's own
  // Back button does that now, so a second tap re-opens rather than toggling
  // into a state where the pill looks active with nothing on screen.
  lawActiveEntry = id;
  pendingModalOpen = true;
  renderLawContent();
}

/** Jump to the Law tab and open a specific authority (called from citation chips). */
export function goToLaw(entryId: string): void {
  const entry = CASELAW.find(function (c) {
    return c.id === entryId;
  });
  if (!entry) return;
  // Set sub-tab to match the entry type
  lawActiveSubTab = entry.type; // 'statute' | 'caselaw' | 'rule'
  // Update sub-tab button active state
  const typeToKey: Record<string, string> = { caselaw: 'case', statute: 'stat', rule: 'rule' };
  (['all', 'caselaw', 'statute', 'rule'] as const).forEach(function (t) {
    const k = typeToKey[t] || t;
    const btn = byId('ltab-law-' + k);
    if (btn) btn.classList.toggle('active', t === lawActiveSubTab);
  });
  // Open the entry. The authority opens in the modal, so there is nothing to
  // scroll to any more — the old scrollIntoView existed only because the detail
  // rendered below the pill row.
  lawActiveEntry = entryId;
  pendingModalOpen = true;
  showPage('law');
  renderLawContent();
}

function renderLawContent(): void {
  const charges = detectedCharges();
  const container = byId('law-content');
  if (!container) return;

  // Filter: charge-relevant + sub-tab type filter
  let entries = CASELAW.filter(function (c) {
    if (lawActiveSubTab !== 'all' && c.type !== lawActiveSubTab) return false;
    if (c.charges === 'all') return true;
    if (!charges.length) return true;
    return (
      Array.isArray(c.charges) &&
      c.charges.some(function (ch) {
        return charges.indexOf(ch) > -1;
      })
    );
  });

  if (!entries.length) {
    container.innerHTML =
      '<div class="card"><p style="color:var(--text-secondary);font-size:13px;font-style:italic;margin:0;">No authorities in this category for the current charges. Enter a charge on the Case tab to filter results.</p></div>';
    return;
  }

  // Sort: statutes first, then case law, then rules
  const typeOrder: Record<string, number> = { statute: 0, caselaw: 1, rule: 2 };
  entries = entries.slice().sort(function (a, b) {
    return (typeOrder[a.type] || 0) - (typeOrder[b.type] || 0);
  });

  const typeColors: Record<string, string> = { caselaw: '#1d4ed8', statute: '#065f46', rule: '#7c3aed' };
  const typeLabels: Record<string, string> = { caselaw: 'Case Law', statute: 'Statute', rule: 'Court Rule' };
  const chargeColorMap: Record<string, string> = {
    murder: '#991b1b',
    drug: '#7c3aed',
    fraud: '#92400e',
    assault: '#b45309',
    oui: '#0369a1',
    sexual: '#9d174d'
  };

  // ── PILL ROW ──────────────────────────────────────────
  let pillHtml =
    '<div style="display:flex;flex-wrap:wrap;gap:8px;padding:12px 14px 10px;border-bottom:1px solid #e2e8f0;margin-bottom:14px;">';
  entries.forEach(function (c) {
    const badgeColor = typeColors[c.type] || '#334155';
    const isActive = lawActiveEntry === c.id;
    // Short label: use citation but trim to ~30 chars for pill readability
    const label = c.citation.length > 38 ? c.citation.slice(0, 36) + '…' : c.citation;
    const q = "'";
    pillHtml +=
      '<button onclick="selectLawEntry(' +
      q +
      c.id +
      q +
      ')" style="' +
      'background:' +
      (isActive ? badgeColor : '#f1f5f9') +
      ';' +
      'color:' +
      (isActive ? '#fff' : '#334155') +
      ';' +
      'border:' +
      (isActive ? '2px solid ' + badgeColor : '2px solid #e2e8f0') +
      ';' +
      'border-radius:20px;padding:5px 12px;font-size:11px;font-weight:' +
      (isActive ? '700' : '600') +
      ';' +
      'cursor:pointer;white-space:nowrap;transition:all 0.15s;line-height:1.3;">' +
      escH(label) +
      '</button>';
  });
  pillHtml += '</div>';

  // ── DETAIL PANEL ──────────────────────────────────────
  let detailHtml = '';
  const active = entries.find(function (c) {
    return c.id === lawActiveEntry;
  });
  if (!active && entries.length) {
    // Nothing selected yet — show instruction
    detailHtml =
      '<div class="card" style="padding:18px;text-align:center;">' +
      '<p style="color:#94a3b8;font-size:13px;margin:0;">Select an authority above to view the full text with highlighted applicable provisions.</p>' +
      '</div>';
  } else if (active) {
    const badgeColor = typeColors[active.type] || '#334155';
    const badgeLabel = typeLabels[active.type] || active.type;

    // Charge chips
    let chargeChips = '';
    if (active.charges === 'all') {
      chargeChips =
        '<span style="background:#e2e8f0;color:#475569;border-radius:10px;padding:2px 8px;font-size:10px;font-weight:600;">All Charges</span>';
    } else if (Array.isArray(active.charges)) {
      chargeChips = active.charges
        .map(function (ch) {
          const lbl = CHARGES[ch] ? CHARGES[ch].label : ch;
          const col = chargeColorMap[ch] || '#334155';
          return (
            '<span style="background:' +
            col +
            '22;color:' +
            col +
            ';border-radius:10px;padding:2px 8px;font-size:10px;font-weight:600;">' +
            lbl +
            '</span>'
          );
        })
        .join('');
    }

    detailHtml += '<div class="card" style="padding:16px;">';

    // Header
    detailHtml += '<div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:10px;flex-wrap:wrap;">';
    detailHtml +=
      '<span style="background:' +
      badgeColor +
      ';color:#fff;border-radius:4px;padding:2px 8px;font-size:10px;font-weight:700;white-space:nowrap;flex-shrink:0;">' +
      badgeLabel +
      '</span>';
    detailHtml +=
      '<a href="' +
      active.url +
      '" target="_blank" rel="noopener" style="font-size:14px;font-weight:700;color:#1e293b;text-decoration:none;line-height:1.4;flex:1;min-width:0;">' +
      escH(active.citation) +
      ' <span style="font-size:11px;color:#3b82f6;font-weight:400;">↗ Open Source</span>' +
      '</a>';
    detailHtml += '</div>';

    // Verification disclaimer — leads every case / rule entry (curated summaries).
    if (active.type === 'caselaw' || active.type === 'rule') {
      detailHtml +=
        '<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px 12px;margin-bottom:12px;">' +
        '<div style="font-size:10px;font-weight:700;color:#b45309;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:4px;">⚠ Curated quick reference</div>' +
        '<p style="font-size:11px;color:#78350f;margin:0;line-height:1.6;">These summaries are curated for quick reference. Verify each citation and holding against the official reporter before relying on it.</p>' +
        '</div>';
    }

    // Charge chips
    if (chargeChips) {
      detailHtml +=
        '<div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:10px;">' + chargeChips + '</div>';
    }

    // SUMMARY BLOCK
    detailHtml +=
      '<div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:12px 14px;margin-bottom:14px;">';
    detailHtml +=
      '<div style="font-size:10px;font-weight:700;color:#1d4ed8;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:6px;">Summary</div>';
    detailHtml +=
      '<p style="font-size:12px;color:#1e3a5f;margin:0;line-height:1.7;">' + escH(active.summary) + '</p>';
    detailHtml += '</div>';

    // KEY HOLDINGS
    detailHtml +=
      '<div style="background:#f8fafc;border-left:3px solid ' +
      badgeColor +
      ';border-radius:0 8px 8px 0;padding:10px 12px;margin-bottom:14px;">';
    detailHtml +=
      '<div style="font-size:10px;font-weight:700;color:' +
      badgeColor +
      ';text-transform:uppercase;letter-spacing:0.6px;margin-bottom:6px;">Key Holdings</div>';
    active.holdings.forEach(function (h, i) {
      detailHtml +=
        '<div style="display:flex;gap:8px;' +
        (i > 0 ? 'margin-top:6px;padding-top:6px;border-top:1px solid #e2e8f0;' : '') +
        '">';
      detailHtml +=
        '<span style="flex-shrink:0;width:18px;height:18px;border-radius:50%;background:' +
        badgeColor +
        ';color:#fff;font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center;">' +
        (i + 1) +
        '</span>';
      detailHtml +=
        '<span style="font-size:11px;color:#1e293b;line-height:1.6;">' + escH(h) + '</span>';
      detailHtml += '</div>';
    });
    detailHtml += '</div>';

    // FULL TEXT with highlighted applicable segments
    if (active.fullText) {
      detailHtml += '<div style="margin-top:4px;">';
      detailHtml +=
        '<div style="font-size:10px;font-weight:700;color:' +
        badgeColor +
        ';text-transform:uppercase;letter-spacing:0.6px;margin-bottom:8px;">Full Text — Applicable Provisions Highlighted</div>';
      detailHtml +=
        '<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;font-size:12px;color:#1e293b;line-height:1.9;font-family:Georgia,serif;">';
      detailHtml += highlightLawText(active.fullText, active.applicableSegments || []);
      detailHtml += '</div>';
      // Legend
      detailHtml += '<div style="display:flex;align-items:center;gap:6px;margin-top:8px;">';
      detailHtml +=
        '<mark style="background:#fef08a;padding:1px 6px;border-radius:3px;font-size:10px;font-weight:600;">highlighted</mark>';
      detailHtml += '<span style="font-size:10px;color:#94a3b8;">= provisions applicable to this case</span>';
      detailHtml += '</div>';
      detailHtml += '</div>';
    }

    // Per-entry action buttons
    const q2 = "'";
    detailHtml +=
      '<div style="display:flex;gap:7px;margin-top:14px;">' +
      '<button class="btn btn-secondary" style="flex:1;" onclick="printLawEntry(' +
      q2 +
      active.id +
      q2 +
      ')">' +
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6,9 6,2 18,2 18,9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg> Print</button>' +
      '<button class="btn btn-secondary" style="flex:1;" onclick="saveLawEntry(' +
      q2 +
      active.id +
      q2 +
      ')">' +
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17,21 17,13 7,13 7,21"/></svg> Save</button>' +
      // .docx removed per user request
      // .docx label removed
      '</div>';

    detailHtml += '</div>'; // card
  }

  // The authority opens in the modal, not underneath the pills.
  //
  // It used to render inline below the pill row, which put a full opinion at the
  // BOTTOM of the page: you tapped a pill at the top and then had to scroll past
  // everything to read it, with no way to dismiss it but tapping the pill again.
  // The modal has a sticky Back button in its chrome and the entry's own Print /
  // Save buttons, so it closes and prints from the top.
  //
  // The page keeps the pills and the "select an authority" placeholder, so the
  // tab still looks like a list of authorities rather than going blank.
  container.innerHTML = pillHtml + (active ? '' : detailHtml);
  void linkifyElement(container);

  // Only when the user actually picked something. renderLawContent() also runs
  // on tab and sub-tab switches, and re-opening the modal on those would make
  // the Law tab impossible to browse.
  if (active && pendingModalOpen) {
    pendingModalOpen = false;
    // Print and Save repeated at the TOP, beside the sticky Back button. The
    // entry's own copies sit at the foot of the card, which on a full opinion is
    // several screens of scrolling away — no use to someone who opened it to
    // print it. Sticky so they stay reachable however far down you read.
    const q3 = "'";
    const toolbar =
      '<div style="position:sticky;top:34px;z-index:3;display:flex;gap:7px;background:#fff;' +
      'padding:0 0 9px;margin-bottom:2px;border-bottom:1px solid #e2e8f0;">' +
      '<button class="btn btn-secondary btn-sm" style="flex:1;" onclick="printLawEntry(' +
      q3 + active.id + q3 + ')">🖨 Print</button>' +
      '<button class="btn btn-secondary btn-sm" style="flex:1;" onclick="saveLawEntry(' +
      q3 + active.id + q3 + ')">⬇ Save</button>' +
      '</div>';
    showModal(toolbar + detailHtml);
    const box = byId('modal-content');
    if (box) void linkifyElement(box);
  }
}

// ── Text builders ────────────────────────────────────────────────────────
const TEXT_TYPE_LABELS: Record<string, string> = {
  caselaw: 'Case Law',
  statute: 'Statute',
  rule: 'Court Rule'
};

// ── LAW TAB: build text for a single entry ───────────────────────────────────
function buildLawEntryText(id: string): string {
  const c = CASELAW.find(function (e) {
    return e.id === id;
  });
  if (!c) return '';
  let out = '[' + (TEXT_TYPE_LABELS[c.type] || c.type).toUpperCase() + '] ' + c.citation + '\n';
  out += '='.repeat(60) + '\n\n';
  if (c.type === 'caselaw' || c.type === 'rule') {
    out += 'NOTE: Curated quick-reference summary. Verify each citation and holding\nagainst the official reporter before relying on it.\n\n';
  }
  out += 'SUMMARY:\n' + c.summary + '\n\n';
  out += 'KEY HOLDINGS:\n';
  c.holdings.forEach(function (h, idx) {
    out += idx + 1 + '. ' + h + '\n';
  });
  out += '\n';
  if (c.fullText) {
    out += 'FULL TEXT:\n' + c.fullText + '\n\n';
  }
  if (c.url) {
    out += 'SOURCE: ' + c.url + '\n';
  }
  return out;
}

// ── LAW TAB: ALL entries (ignores sub-tab filter) ─────────────────────────────
function buildLawAllText(): string {
  const charges = detectedCharges();
  let entries = CASELAW.filter(function (c) {
    if (c.charges === 'all') return true;
    if (!charges.length) return true;
    return (
      Array.isArray(c.charges) &&
      c.charges.some(function (ch) {
        return charges.indexOf(ch) > -1;
      })
    );
  });
  const typeOrder: Record<string, number> = { statute: 0, caselaw: 1, rule: 2 };
  entries = entries.slice().sort(function (a, b) {
    return (typeOrder[a.type] || 0) - (typeOrder[b.type] || 0);
  });
  let out = 'LAW TAB — ALL AUTHORITIES\n' + '='.repeat(60) + '\n\n';
  entries.forEach(function (c) {
    out += '[' + (TEXT_TYPE_LABELS[c.type] || c.type).toUpperCase() + '] ' + c.citation + '\n';
    out += '-'.repeat(50) + '\n';
    if (c.type === 'caselaw' || c.type === 'rule') {
      out += 'NOTE: Curated quick-reference summary. Verify each citation and holding\nagainst the official reporter before relying on it.\n\n';
    }
    out += 'SUMMARY:\n' + c.summary + '\n\n';
    out += 'KEY HOLDINGS:\n';
    c.holdings.forEach(function (h, idx) {
      out += idx + 1 + '. ' + h + '\n';
    });
    out += '\n';
    if (c.fullText) {
      out += 'FULL TEXT:\n' + c.fullText + '\n';
    }
    if (c.url) {
      out += 'SOURCE: ' + c.url + '\n';
    }
    out += '\n' + '='.repeat(60) + '\n\n';
  });
  return out;
}

// ── Local doc-viewer fallbacks ───────────────────────────────────────────────
// TODO: route through doc viewer (saveDocAsText / openPrintWindow from
// '@/documents/doc-viewer') once that module is ready.
function downloadText(filename: string, text: string): void {
  const fname = filename.replace(/\.[^.]+$/, '') + '.txt';
  const blob = new Blob([text], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fname;
  a.click();
  setTimeout(function () {
    URL.revokeObjectURL(a.href);
  }, 1000);
  showToast('Saved as ' + fname);
}

function printText(title: string, text: string): void {
  // Prints through a hidden iframe rather than window.open('', '_blank'), which
  // browsers block as a pop-up — see ui/print.ts.
  printPlainText(title, text);
}

// ── LAW TAB: ALL entries (ignores sub-tab filter) ─────────────────────────────
export function printLawAll(): void {
  printText('LAW — ALL LEGAL AUTHORITIES', buildLawAllText());
}
export function saveLawAll(): void {
  downloadText('law-all-authorities', buildLawAllText());
}

// ── LAW TAB: per-entry buttons ───────────────────────────────────────────────
export function printLawEntry(id: string): void {
  const c = CASELAW.find(function (e) {
    return e.id === id;
  });
  if (!c) return;
  printText(c.citation, buildLawEntryText(id));
}
export function saveLawEntry(id: string): void {
  const c = CASELAW.find(function (e) {
    return e.id === id;
  });
  if (!c) return;
  const slug = c.citation
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .slice(0, 40);
  downloadText(slug, buildLawEntryText(id));
}
